import React from 'react';
import {
    Checkbox,
    FormControlLabel,
    Select,
    MenuItem,
    Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const CHANGE_STATES = ['New', 'Assess', 'Authorize', 'Scheduled', 'Implement', 'Review', 'Closed', 'Canceled'];
const EMAIL_FREQUENCIES = [
    { value: 'instant', label: 'Instant' },
    { value: 'twiceDaily', label: 'Twice Daily' },
    { value: 'Daily', label: 'Daily' },
    { value: 'Weekly', label: 'Weekly (Every Friday)' },
    { value: 'Biweekly', label: 'Biweekly (Wednesday & Friday)' },
];

/**
 * ApplicationSettingsRow Component
 * Expandable row for viewing/editing application notification settings
 */
const ApplicationSettingsRow = ({
    application,
    isExpanded,
    isLoading = false,
    onToggleExpand,
    onUpdateSettings,
    onSave,
    onUnsubscribe,
    onRemoveFromList,
    onAddToList,
    permission = 'owner', // 'owner', 'write', or 'read'
    isListContext = false, // Whether this is within a subscription list
    isSaving = false,
    hasChanges = false,
}) => {
    const canEdit = permission === 'owner' || permission === 'write';
    const settings = application.notificationSettings || {};
    // Hide upstream/downstream checkboxes if applicationID contains 'not applicable'
    const hideUpDownstream = Array.isArray(settings.applicationID) && settings.applicationID.includes('not applicable');

    const handleCheckboxChange = (field) => {
        if (!canEdit) return;
        onUpdateSettings(application.id, { [field]: !settings[field] });
    };

    const handleStateChange = (state) => {
        if (!canEdit) return;
        const currentStates = settings.subscribedStates || [];
        const newStates = currentStates.includes(state)
            ? currentStates.filter((s) => s !== state)
            : [...currentStates, state];
        onUpdateSettings(application.id, { subscribedStates: newStates });
    };

    const handleNotificationTypeChange = () => {
        if (!canEdit) return;
        const newType = settings.notificationType === 'false' ? 'instant' : 'false';
        onUpdateSettings(application.id, { notificationType: newType });
    };

    const handleFrequencyChange = (event) => {
        if (!canEdit) return;
        onUpdateSettings(application.id, { notificationType: event.target.value });
    };

    return (
        <div className="application-settings-row">
            {/* Header */}
            <div
                className="app-settings-header"
                onClick={() => onToggleExpand(application.id, application.name)}
            >
                <div className="app-settings-header-left">
                    {isExpanded ? (
                        <ExpandLessIcon className="expand-icon" />
                    ) : (
                        <ExpandMoreIcon className="expand-icon" />
                    )}
                    <span className="app-name">{application.name}</span>
                    {application.addedBy && (
                        <span className="app-meta" style={{ 
                            fontSize: '12px', 
                            color: '#666', 
                            marginLeft: '12px',
                            fontStyle: 'italic'
                        }}>
                            Added by {application.addedBy.user_name}
                        </span>
                    )}
                </div>

                <div className="app-settings-header-right">
                    {!isListContext && onAddToList && (
                        <button
                            className="add-to-list-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddToList(application);
                            }}
                        >
                            + Add to Subscription List
                        </button>
                    )}
                    {isListContext && onRemoveFromList && canEdit && (
                        <button
                            className="remove-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemoveFromList(application);
                            }}
                        >
                            Remove
                        </button>
                    )}
                </div>
            </div>

            {/* Expanded Settings */}
            {isExpanded && (
                <div className="app-settings-content">
                    {isLoading ? (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
                            <div style={{ textAlign: 'center', margin: '20px' }}>
                                <div className="loader"></div>
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                            <div style={{ flex: 0.5, margin: '10px' }}>
                                <strong>App ID:</strong> {application.appId || 'N/A'}
                            </div>
                            {!hideUpDownstream && (
                                <div style={{ flex: 1, margin: '10px' }}>
                                    <strong>Upstream/Downstream Applications:</strong>
                                    <div style={{ display: "flex", flexDirection: "column", marginTop: "5px" }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={settings.upstreamSubscribed || false}
                                                    onChange={() => handleCheckboxChange('upstreamSubscribed')}
                                                    disabled={!canEdit}
                                                />
                                            }
                                            label="Subscribe to Upstream Applications"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={settings.downstreamSubscribed || false}
                                                    onChange={() => handleCheckboxChange('downstreamSubscribed')}
                                                    disabled={!canEdit}
                                                />
                                            }
                                            label="Subscribe to Downstream Applications"
                                        />
                                    </div>
                                </div>
                            )}
                            <div style={{ flex: 0.7, margin: '10px' }}>
                                <strong>Subscribed States:</strong>
                                <div className="custom-scrollbar" style={{ display: "flex", flexDirection: "column", maxHeight: "100px", overflowY: "auto", marginTop: "5px" }}>
                                    {["New", "Assess", "Authorize", "Scheduled", "Implement", "Review", "Closed", "Canceled"].map((state) => (
                                        <FormControlLabel
                                            key={state}
                                            control={
                                                <Checkbox
                                                    checked={(settings.subscribedStates || []).includes(state)}
                                                    onChange={() => handleStateChange(state)}
                                                    disabled={!canEdit}
                                                />
                                            }
                                            label={state}
                                        />
                                    ))}
                                </div>
                            </div>
                            <div style={{ flex: 1, display: "flex", flexDirection: "column", margin: '10px' }}>
                                <strong>Notifications:</strong>
                                <div style={{ marginTop: "5px" }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={settings.notificationType !== "false"}
                                                onChange={handleNotificationTypeChange}
                                                disabled={!canEdit}
                                            />
                                        }
                                        label="Receive Notifications via Email"
                                    />
                                </div>
                            </div>
                            {settings.notificationType !== "false" && (
                                <div style={{ flex: 1, margin: '10px' }}>
                                    <strong>Email Notifications Frequency:</strong>
                                    <Select
                                        value={settings.notificationType || 'instant'}
                                        onChange={handleFrequencyChange}
                                        disabled={!canEdit}
                                        style={{ width: "200px", height: "40px" }}
                                    >
                                        <MenuItem value="instant">Instant</MenuItem>
                                        <MenuItem value="twiceDaily">Twice Daily</MenuItem>
                                        <MenuItem value="Daily">Daily</MenuItem>
                                        <MenuItem value="Weekly">Weekly (Every Friday)</MenuItem>
                                        <MenuItem value="Biweekly">Biweekly (Wednesday & Friday)</MenuItem>
                                    </Select>
                                </div>
                            )}
                            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                                
                                {!isListContext && onUnsubscribe && (
                                    <Button 
                                        variant="contained" 
                                        style={{ marginBottom: "10px", backgroundColor: "#FA6A55" }}
                                        onClick={() => onUnsubscribe(application)}
                                        disabled={isSaving}
                                    >
                                        Unsubscribe
                                    </Button>
                                )}
                                {canEdit && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        disabled={!hasChanges || isSaving}
                                        onClick={() => onSave(application)}
                                    >
                                        {isSaving ? 'Saving...' : 'Save Changes'}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ApplicationSettingsRow;
